import {
    h,
    Host,
    State,
    Prop,
    Event,
    EventEmitter,
    Watch,
    Component,
    Listen,
} from "@stencil/core";
import { AuthClientInstance, getAuthClient } from "@trumpf/ux.utils";
import {
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    onDropZoneDragLeave,
    onDropZoneDragOver,
    onDropZoneDrop,
    resetDragState,
} from "./drag-handlers";

// Constants
const API_VERSION = "1.0";
const DEFAULT_SCOPE = "mytrumpf";

// Types
export interface ImageItem {
    url: string;
    relatedType?: string;
    tags?: { [key: string]: string };
    file?: File; // Add for uploaded files
    [key: string]: any;
}

export interface ApiResponse {
    imageFiles?: ImageItem[] | ImageItem;
}

export interface UploadMetadata {
    changedBy: string;
}
@Component({
    tag: "ux-iws-asset-image-manager",
    styleUrl: "asset-image-manager.scss",
    shadow: false,
})
export class AssetImageManager {
    private baseClass = "ux-iws-asset-image-manager";

    /** API endpoint for fetching and submitting images */
    @Prop() apiBaseUrl: string;
    /** asset id for fetching and submitting images */
    @Prop() assetId: string;
    /** Sets drag and drop option (wip) */
    @Prop() dragAndDrop: boolean = false;
    /** the http header for the image request */
    @Prop() header: object = {};
    /** authentication scope to use */
    @Prop() scope: string = null;
    /** log console warnings, messages */
    @Prop() debug: boolean = false;
    /** The bearer token to use for authentication */
    @State() token: string = null;

    @State() images: ImageItem[] = [];
    @State() dragIndex: number = null;
    @State() dropTargetIndex: number = null;
    @State() isDragging: boolean = false;
    @State() fileUploadKey: number = 0;

    @Event() imagesChanged: EventEmitter<string[]>;
    private authClient: AuthClientInstance = null;

    async componentWillLoad() {
        this.authClient = getAuthClient(this.scope);
        this.token = this.authClient.getAccessToken();

        // Wait for token before attempting to fetch images
        if (this.token) {
            this.getImages();
        }
    }

    @Watch("apiBaseUrl")
    @Watch("header")
    @Watch("scope")
    @Watch("token")
    private async getImages() {
        if (!this.apiBaseUrl || !this.assetId) {
            if (this.debug) {
                console.warn("Missing apiBaseUrl or assetId");
            }
            return;
        }

        if (!this.token && Object.keys(this.header).length === 0) {
            if (this.debug) {
                console.warn("No authentication token or headers provided");
            }
            return;
        }

        if (!this.authClient) {
            if (this.debug) {
                console.error("Auth client not initialized");
            }
            return;
        }

        try {
            const headers = new Headers();

            if (this.token) {
                headers.append("Authorization", `Bearer ${this.token}`);
            }

            Object.entries(this.header).forEach(([key, value]) => {
                if (value) {
                    // Only append non-empty values
                    headers.append(key, String(value));
                }
            });

            const fetchUrl = `${this.apiBaseUrl}/core/assets/${this.assetId}?api-version=${API_VERSION}`;

            const response = await fetch(fetchUrl, { headers });

            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`
                );
            }

            const data: ApiResponse = await response.json();
            const imageData = data.imageFiles;

            if (!imageData) {
                if (this.debug) {
                    console.warn("No imageFiles in API response");
                }
                this.images = [];
                this.imagesChanged.emit([]);
                return;
            }

            const images: ImageItem[] = Array.isArray(imageData)
                ? imageData.map(this.normalizeImageItem.bind(this))
                : [this.normalizeImageItem(imageData)];

            this.ensureDisplayImageExists(images);
            this.images = images;
            this.imagesChanged.emit(images.map((img) => img.url));
        } catch (error) {
            console.error("Failed to fetch images:", error);
            // Could emit an error event here for parent components to handle
        }
    }

    private normalizeImageItem(img: any): ImageItem {
        if (typeof img === "string") {
            return { url: img };
        }

        let imageUrl = img.url;
        if (imageUrl && !imageUrl.startsWith("http")) {
            imageUrl = `${this.apiBaseUrl}${imageUrl}`;
        }

        return { ...img, url: imageUrl };
    }

    private ensureDisplayImageExists(images: ImageItem[]) {
        const hasDisplayImage = images.some(
            (img) => img.tags?.displayImage === "true"
        );

        if (!hasDisplayImage) {
            const assetTypeImg = images.find(
                (img) => img.relatedType === "AssetType"
            );
            if (assetTypeImg) {
                if (!assetTypeImg.tags) assetTypeImg.tags = {};
                assetTypeImg.tags.displayImage = "true";
            }
        }
    }

    private handleToken = (token: string | null) => {
        this.token = token;
        if (token) {
            this.getImages();
        }
    };

    private async handleImageRemove(index: number) {
        if (index < 0 || index >= this.images.length) {
            if (this.debug) {
                console.error("Invalid image index", index);
            }
            return;
        }

        const image = this.images[index];
        if (!image) {
            console.error("No image found at index", index);
            return;
        }

        // For local images (uploaded but not saved to API), just remove locally
        if (!image.relatedType || !image.url.startsWith("http")) {
            this.removeImageFromState(index);
            return;
        }

        const fileName = this.getFileName(image.url);
        if (!this.assetId || !fileName) {
            if (this.debug) {
                console.error("Missing assetId or fileName for API deletion");
            }
            return;
        }

        try {
            await this.deleteImageFromAPI(fileName);
            this.removeImageFromState(index);
        } catch (error) {
            console.error("Failed to delete image:", error);
            // Could show user-facing error here
        }
    }

    private async deleteImageFromAPI(fileName: string): Promise<void> {
        const headers: Record<string, string> = {};
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        const encodedFileName = encodeURIComponent(fileName);
        const deleteUrl = `${this.apiBaseUrl}/core/assets/${this.assetId}/media/images/${encodedFileName}?api-version=${API_VERSION}`;

        const response = await fetch(deleteUrl, {
            method: "DELETE",
            headers,
        });

        if (!response.ok) {
            throw new Error(
                `Delete failed: ${response.status} ${response.statusText}`
            );
        }
    }

    private removeImageFromState(index: number) {
        this.images = [
            ...this.images.slice(0, index),
            ...this.images.slice(index + 1),
        ];
        this.imagesChanged.emit(this.images.map((img) => img.url));
    }

    @Listen("uxFileChange")
    async handleFileChange(event: CustomEvent<{ files: File[] }>) {
        const files = event.detail.files;
        if (!files?.length) return;

        const apiImages = this.images.filter((img) => img.relatedType);
        const changedBy = this.token || "unknown";

        try {
            const uploadedImages = await this.uploadFiles(files, changedBy);
            this.images = [...apiImages, ...uploadedImages];
            this.imagesChanged.emit(this.images.map((img) => img.url));
            this.fileUploadKey++; // Reset the file upload component
        } catch (error) {
            console.error("Failed to upload files:", error);
            // Could emit an error event here
        }
    }

    private async uploadFiles(
        files: File[],
        changedBy: string
    ): Promise<ImageItem[]> {
        if (!this.apiBaseUrl || !this.assetId) {
            throw new Error("Missing apiBaseUrl or assetId");
        }

        const headers: Record<string, string> = {};
        if (this.token) {
            headers.Authorization = `Bearer ${this.token}`;
        }

        const uploadedImages: ImageItem[] = [];

        for (const file of files) {
            try {
                const uploadedImage = await this.uploadSingleFile(
                    file,
                    changedBy,
                    headers
                );
                uploadedImages.push(uploadedImage);
            } catch (error) {
                console.error(`Failed to upload file ${file.name}:`, error);
                // Create fallback local image for failed uploads
                uploadedImages.push({
                    url: URL.createObjectURL(file),
                    relatedType: undefined,
                    tags: {},
                    file,
                });
            }
        }

        return uploadedImages;
    }

    private async uploadSingleFile(
        file: File,
        changedBy: string,
        headers: Record<string, string>
    ): Promise<ImageItem> {
        const fetchUrl = `${this.apiBaseUrl}/core/assets/${this.assetId}/media/images?api-version=${API_VERSION}`;
        const formData = new FormData();
        formData.append("file", file);
        formData.append(
            "metadata",
            JSON.stringify({ changedBy } as UploadMetadata)
        );

        const response = await fetch(fetchUrl, {
            method: "POST",
            headers,
            body: formData,
        });

        if (!response.ok) {
            throw new Error(
                `Upload failed: ${response.status} ${response.statusText}`
            );
        }

        const data = await response.json();
        const imageData = Array.isArray(data) ? data[0] : data;

        let imageUrl = imageData.url;
        if (imageUrl && !imageUrl.startsWith("http")) {
            imageUrl = `${this.apiBaseUrl}${imageUrl}`;
        }

        return {
            ...imageData,
            url: imageUrl,
            file,
        };
    }

    connectedCallback() {
        this.authClient = getAuthClient(this.scope);
        this.authClient.subscribe(this.handleToken);
        this.token = this.authClient.getAccessToken();
    }

    disconnectedCallback() {
        this.authClient?.unsubscribe(this.handleToken);
    }

    private getFileName(url: string): string {
        if (!url) return "";

        try {
            // Handle both blob URLs and regular URLs
            const urlPath = url.includes("blob:") ? url : url.split("?")[0]; // Remove query params
            const fileName = urlPath.split("/").pop() || "";
            return decodeURIComponent(fileName);
        } catch (error) {
            if (this.debug) {
                console.error(
                    "Error extracting filename from URL:",
                    url,
                    error
                );
            }
            return "";
        }
    }

    private async setDisplayImage(selectedImg: ImageItem) {
        if (!this.assetId || !selectedImg) {
            if (this.debug) {
                console.error("Missing assetId or selectedImg");
            }
            return;
        }

        try {
            // Handle AssetType images specially
            if (selectedImg.relatedType === "AssetType") {
                await this.updateNonAssetTypeImages(selectedImg);
                this.updateLocalDisplayImageTags(selectedImg);
                return;
            }

            // Handle regular uploaded images
            await this.updateAllImageDisplayTags(selectedImg);
            this.updateLocalDisplayImageTags(selectedImg);
        } catch (error) {
            if (this.debug) {
                console.error("Error updating display image:", error);
            }
        }
    }

    private async updateNonAssetTypeImages(
        selectedImg: ImageItem
    ): Promise<void> {
        const updatePromises = this.images
            .filter(
                (img) =>
                    img.url !== selectedImg.url &&
                    img.relatedType !== "AssetType"
            )
            .map((img) => this.updateImageDisplayTag(img, false));

        const results = await Promise.allSettled(updatePromises);
        results.forEach((result, index) => {
            if (result.status === "rejected") {
                if (this.debug) {
                    console.error(
                        `Failed to update image at index ${index}:`,
                        result.reason
                    );
                }
            }
        });
    }

    private async updateAllImageDisplayTags(
        selectedImg: ImageItem
    ): Promise<void> {
        const updatePromises = this.images.map((img) => {
            if (img.relatedType === "AssetType") {
                return Promise.resolve(); // Skip AssetType images
            }
            const isSelected = img.url === selectedImg.url;
            return this.updateImageDisplayTag(img, isSelected);
        });

        const results = await Promise.allSettled(updatePromises);
        const selectedResult = results.find(
            (_, index) => this.images[index].url === selectedImg.url
        );

        if (selectedResult?.status === "rejected") {
            throw new Error(
                "Failed to set display image for the selected image"
            );
        }
    }

    private async updateImageDisplayTag(
        img: ImageItem,
        isDisplayImage: boolean
    ): Promise<void> {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.token}`,
        };

        const fileName = this.getFileName(img.url);
        const response = await fetch(
            `${this.apiBaseUrl}/core/assets/${this.assetId}/media/images/${fileName}/tags?api-version=${API_VERSION}`,
            {
                method: "POST",
                headers,
                body: JSON.stringify({
                    displayImage: isDisplayImage ? "true" : "false",
                }),
            }
        );

        if (!response.ok) {
            throw new Error(
                `Failed to update display tag for ${fileName}: ${response.status}`
            );
        }
    }

    private updateLocalDisplayImageTags(selectedImg: ImageItem) {
        this.images = this.images.map((img) => ({
            ...img,
            tags: {
                ...img.tags,
                displayImage: img.url === selectedImg.url ? "true" : "false",
            },
        }));
    }

    render() {
        const hasDisplayImage = this.images.some(
            (img) => img.tags?.displayImage === "true"
        );

        return (
            <Host class={this.baseClass}>
                {this.dragAndDrop ? (
                    <div class={`${this.baseClass}__images`}>
                        {this.images.map((img, idx) => {
                            const isActive =
                                img.tags?.displayImage === "true" ||
                                (!hasDisplayImage &&
                                    img.relatedType === "AssetType");

                            return (
                                <div key={`img-${idx}`}>
                                    {this.isDragging &&
                                        this.dropTargetIndex === idx && (
                                            <div
                                                class={`${this.baseClass}__drop-indicator`}
                                            />
                                        )}
                                    <div
                                        class={`${this.baseClass}__image`}
                                        draggable={true}
                                        onDragStart={(e) =>
                                            onDragStart(
                                                e,
                                                idx,
                                                (val) => (this.dragIndex = val),
                                                (val) => (this.isDragging = val)
                                            )
                                        }
                                        onDragOver={(e) =>
                                            onDragOver(
                                                e,
                                                idx,
                                                this.dropTargetIndex,
                                                (val) =>
                                                    (this.dropTargetIndex = val)
                                            )
                                        }
                                        onDrop={(e) =>
                                            onDrop(
                                                e,
                                                idx,
                                                this.dragIndex,
                                                this.images,
                                                this.imagesChanged,
                                                (val) => (this.images = val),
                                                () =>
                                                    resetDragState(
                                                        (val) =>
                                                            (this.dragIndex =
                                                                val),
                                                        (val) =>
                                                            (this.dropTargetIndex =
                                                                val)
                                                    )
                                            )
                                        }
                                        onDragEnd={() =>
                                            onDragEnd(
                                                (val) =>
                                                    (this.isDragging = val),
                                                () =>
                                                    resetDragState(
                                                        (val) =>
                                                            (this.dragIndex =
                                                                val),
                                                        (val) =>
                                                            (this.dropTargetIndex =
                                                                val)
                                                    )
                                            )
                                        }
                                        style={{
                                            opacity:
                                                this.dragIndex === idx
                                                    ? "0.5"
                                                    : "1",
                                            cursor: "grab",
                                        }}
                                    >
                                        <button
                                            class={{
                                                [`${this.baseClass}__display-img-btn`]: true,
                                                [`${this.baseClass}__display-img-btn--active`]:
                                                    isActive,
                                            }}
                                            disabled={isActive}
                                            onClick={() =>
                                                this.setDisplayImage(img)
                                            }
                                        >
                                            <span class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                                {isActive
                                                    ? "Anzeigebild"
                                                    : "Auswählen"}
                                            </span>
                                        </button>
                                        <uxc-auth-image
                                            src={img.url}
                                            scope={DEFAULT_SCOPE}
                                        >
                                            <ux-image
                                                alt={`Image ${idx + 1}`}
                                            ></ux-image>
                                        </uxc-auth-image>
                                        {img.relatedType !== "AssetType" && (
                                            <ux-button
                                                size="icon"
                                                icon="bin"
                                                title="Remove image"
                                                variant="no-border"
                                                class="ux-u-background-white"
                                                onClick={() =>
                                                    this.handleImageRemove(idx)
                                                }
                                            ></ux-button>
                                        )}
                                        {img.relatedType === "AssetType" && (
                                            <span
                                                class={`${this.baseClass}__image-disclaimer ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase`}
                                            >
                                                Standard Bild
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div
                            class="drop-zone"
                            onDragOver={(e) =>
                                onDropZoneDragOver(
                                    e,
                                    this.dropTargetIndex,
                                    this.images.length,
                                    (val) => (this.dropTargetIndex = val)
                                )
                            }
                            onDrop={(e) =>
                                onDropZoneDrop(
                                    e,
                                    this.images.length,
                                    (event, idx) =>
                                        onDrop(
                                            event,
                                            idx,
                                            this.dragIndex,
                                            this.images,
                                            this.imagesChanged,
                                            (val) => (this.images = val),
                                            () =>
                                                resetDragState(
                                                    (val) =>
                                                        (this.dragIndex = val),
                                                    (val) =>
                                                        (this.dropTargetIndex =
                                                            val)
                                                )
                                        )
                                )
                            }
                            onDragLeave={(e) =>
                                onDropZoneDragLeave(
                                    e,
                                    (val) => (this.dropTargetIndex = val)
                                )
                            }
                            onDragEnd={() =>
                                onDragEnd(
                                    (val) => (this.isDragging = val),
                                    () =>
                                        resetDragState(
                                            (val) => (this.dragIndex = val),
                                            (val) =>
                                                (this.dropTargetIndex = val)
                                        )
                                )
                            }
                        ></div>
                    </div>
                ) : (
                    <div class={`${this.baseClass}__images`}>
                        {this.images.map((img, idx) => {
                            const isActive =
                                img.tags?.displayImage === "true" ||
                                (!hasDisplayImage &&
                                    img.relatedType === "AssetType");

                            return (
                                <div
                                    class={`${this.baseClass}__image`}
                                    key={`img-${idx}`}
                                >
                                    <button
                                        class={{
                                            [`${this.baseClass}__display-img-btn`]: true,
                                            [`${this.baseClass}__display-img-btn--active`]:
                                                isActive,
                                        }}
                                        disabled={isActive}
                                        onClick={() =>
                                            this.setDisplayImage(img)
                                        }
                                    >
                                        <span class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                            {isActive
                                                ? "Anzeigebild"
                                                : "Auswählen"}
                                        </span>
                                    </button>
                                    <uxc-auth-image
                                        src={img.url}
                                        scope={DEFAULT_SCOPE}
                                    >
                                        <ux-image
                                            alt={`Image ${idx + 1}`}
                                        ></ux-image>
                                    </uxc-auth-image>
                                    {img.relatedType !== "AssetType" && (
                                        <ux-button
                                            size="icon"
                                            icon="bin"
                                            title="Remove image"
                                            variant="no-border"
                                            class="ux-u-background-white"
                                            onClick={() =>
                                                this.handleImageRemove(idx)
                                            }
                                        ></ux-button>
                                    )}
                                    {img.relatedType === "AssetType" && (
                                        <span
                                            class={`${this.baseClass}__image-disclaimer ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase`}
                                        >
                                            Standard Bild
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                <ux-file-upload
                    key={this.fileUploadKey}
                    accepted-files="image/*,application/pdf,text/html"
                    label="File Upload"
                    max-files="5"
                    max-file-size="2"
                    name="file"
                    style={{ "--ux-file-upload-item-alignment": "flex-start" }}
                ></ux-file-upload>
            </Host>
        );
    }
}
